import { FlatfileProvider, Space } from "@flatfile/react";
import {
  BulkPayableStatus,
  BulkStatus,
  IBulkPayableBatch
} from "@wingspanhq/payments/dist/interfaces";
import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { useMemo } from "react";
import { useRouteMatch } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";

import { useAccountAccess } from "@hooks/useAccountAccess";
import { hybridEncrypt } from "@wingspanhq/utils/dist/crypto";
import { queriesEngagements } from "@query";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryUserProfile } from "../../../../query/users/queries";
import { pullSessionToken } from "../../../../services/sessionStorage";
import {
  IS_DEV_ENV,
  IS_PRODUCTION_ENV
} from "../../../../shared/constants/environment";
import { themeConfig } from "../../../../shared/flatfile/themeConfig";
import { useNonContractorXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { selectorIsEngagementsActive } from "../../../../shared/selectors/selectorIsEngagementsActive";
import {
  BULK_IMPORT_CONTRACTORS_NAMESPACE,
  BULK_IMPORT_PAYABLES_NAMESPACE
} from "../../constants";
import { getBulkQueriesByResource } from "../../query/bulkBatchResourceQueryMap";
import { BulkCollaboratorOpenBatchHeader } from "../../routes/BulkImportCollaboratorBatchDetails/BulkCollaboratorOpenBatchHeader";
import { BulkPayableOpenBatchHeader } from "../../routes/BulkImportPayableBatchDetails/BulkPayableOpenBatchHeader";
import { BulkResource } from "../../types";
import { isUploadInProgress } from "../../utils/bulkBatchUtils";
import { flatfileProviderConfig } from "../../utils/flatfileThemeConfig";
import { getBatchCreatorName } from "../../utils/getBatchCreator";
import { DefaultHeader } from "./DefaultHeader";

interface BulkBatchDetailsHeaderProps {
  bulkResource: BulkResource;
}

export function BulkBatchDetailsHeader({
  bulkResource
}: BulkBatchDetailsHeaderProps) {
  const match = useRouteMatch<{ bulkBatchId: string }>();
  const { bulkBatchId } = match.params;

  const { useBulkBatch } = getBulkQueriesByResource(bulkResource);
  const qBulkBatch = useBulkBatch(bulkBatchId);
  const qFeatureFlags = useFeatureFlags();
  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);
  const xWingspanExpansion = useNonContractorXWingspanExpansion();
  const { isImpersonatedByWingspanAdmin } = useAccountAccess();

  // encrypt the API token for storage in the metadata
  const publicKey = process.env.REACT_APP_ENCRYPTION_PUBLIC_KEY as string;
  const originalPublicKey = publicKey.replace(/\\n/g, "\n");

  const apiTokenEncrypted = useMemo(
    () => hybridEncrypt(originalPublicKey, pullSessionToken() as string),
    [originalPublicKey]
  );

  const engagementId = qBulkBatch.data?.labels.engagementId as string;

  const qEngagement = queriesEngagements.getEngagement.useQuery(
    { engagementId },
    { enabled: !!engagementId }
  );

  const selectedEngagement = qEngagement.data;

  const payableStatus = qBulkBatch.data?.labels
    .payableStatus as BulkPayableStatus;

  let OpenBatchHeader = BulkPayableOpenBatchHeader;
  let spaceConfig = {
    name: "Payables Upload",
    namespace: BULK_IMPORT_PAYABLES_NAMESPACE
  };
  if (bulkResource === BulkResource.Collaborator) {
    OpenBatchHeader = BulkCollaboratorOpenBatchHeader;
    spaceConfig = {
      name: "Contractors Upload",
      namespace: BULK_IMPORT_CONTRACTORS_NAMESPACE
    };
  }

  return (
    <WSQueries queries={{ qBulkBatch, qFeatureFlags, queryUser }}>
      {({
        qBulkBatchData: bulkBatch,
        qFeatureFlagsData: featureFlags,
        queryUserData: user
      }) => {
        const ffProviderProps = (bulkBatch.labels[
          BATCH_LABELS_WS_KEYS.flatfileAccessToken
        ] as string)
          ? {
              accessToken: bulkBatch.labels[
                BATCH_LABELS_WS_KEYS.flatfileAccessToken
              ] as string
            }
          : {
              publishableKey: process.env
                .REACT_APP_FLATFILE_PUBLISHABLE_KEY as string
            };
        return (
          <FlatfileProvider
            // Do not set key prop if upload is in progress
            {...(isUploadInProgress(bulkBatch)
              ? {}
              : {
                  key:
                    ffProviderProps.accessToken ??
                    ffProviderProps.publishableKey
                })}
            config={{
              debug: !IS_PRODUCTION_ENV,
              ...flatfileProviderConfig
            }}
            {...ffProviderProps}
          >
            {featureFlags.flatfilePlatform && (
              <Space
                {...(bulkBatch.labels.flatfileBatchId
                  ? { id: bulkBatch.labels.flatfileBatchId as string }
                  : {})}
                config={{
                  ...spaceConfig,

                  // set batch item Id
                  // set api token to use it on listener
                  metadata: {
                    // custom data
                    apiToken: apiTokenEncrypted,
                    customHeaders: xWingspanExpansion
                      ? {
                          "x-wingspan-expansion": xWingspanExpansion
                        }
                      : {},
                    bulkBatchId,

                    isEngagementsEnabled:
                      selectorIsEngagementsActive(user) &&
                      featureFlags.flatfileEngagements,

                    ...(selectedEngagement && {
                      selectedEngagementName: selectedEngagement.name,
                      selectedEngagementId: selectedEngagement.id
                    }),

                    ...(payableStatus && {
                      payableStatus
                    }),

                    // user info
                    userInfo: {
                      userId: bulkBatch.clientId,
                      name: getBatchCreatorName(bulkBatch as IBulkPayableBatch)
                    },

                    // part of configuration
                    sidebarConfig: {
                      // show sidebar for impersonated wingspan admin
                      showSidebar: isImpersonatedByWingspanAdmin || IS_DEV_ENV
                    },
                    ...themeConfig
                  }
                }}
              />
            )}
            {bulkBatch.status === BulkStatus.Open ? (
              <OpenBatchHeader
                bulkBatchId={bulkBatchId}
                bulkResource={bulkResource}
              />
            ) : (
              <DefaultHeader
                bulkBatchId={bulkBatchId}
                bulkResource={bulkResource}
              />
            )}
          </FlatfileProvider>
        );
      }}
    </WSQueries>
  );
}
